import { activity } from './activity';
import { enumItems } from './enumItems';
import { statuses } from './statuses';
import { group } from './group';
import { SchemaWithClient } from '../types/SchemaWithClient';
import { Client } from '../services/Client';
import { Group } from '../types/entities/Group';
import { Activity } from '../types/entities/Activity';
import { NumberField } from '../components/Table/fields/NumberField';
import { SelectField } from '../components/Table/fields/SelectField';
import { DateField } from '../components/Table/fields/DateField';
import { TimeField } from '../components/Table/fields/TimeField';
import { TextField } from '../components/Table/fields/TextField';
import { SetField } from '../components/Table/fields/SetField';
import { guideCost } from './guideCost';
import { serviceProvider } from './serviceProvider';
import { salesPartner } from './salesPartner';
import { activityReservationCost } from './activityReservationCost';
import { StripperReservation } from '../types/entities/StripperReservation';
import { address } from './address';
import { FloatField } from '../components/Table/fields/FloatField';
import { singleStripperReservation } from './singleStripperReservation';
import { SelectFieldWithoutAutocomplete } from '../components/Table/fields/SelectFieldWithoutAutocomplete';

export const stripperReservation = (
  forGroupId?: Group['id'],
  forActivityId?: Activity['id']
): SchemaWithClient<StripperReservation> => {
  return {
    name: 'Artist Reservation',
    pluralName: 'Artist Reservation',
    columns: {
      stripper: {
        name: 'Stripper',
        fieldRenderer: SelectField.tableAdapter(singleStripperReservation),
        switchboard: true,
        required: true,
        tempSchema: singleStripperReservation,
        fieldType: 'simpleSingleSelect',
      },
      guides: {
        name: 'Guides',
        fieldRenderer: SetField.tableAdapter(enumItems.guide),
        multiSwitchboard: true,
        url: enumItems.guide.client.getAllPath,
        tempSchema: enumItems.guide,
      },
      group: {
        name: 'Group',
        required: true,
        fieldRenderer: SelectField.tableAdapter(group),
        switchboard: true,
        url: group.client.getAllPath,
        tempSchema: group,
      },
      activity: {
        name: 'Activity',
        required: true,
        fieldRenderer: SelectField.tableAdapter(activity),
        switchboard: true,
        url: activity.client.getAllPath,
        tempSchema: activity,
      },
      service_provider: {
        name: 'Service Provider',
        fieldRenderer: SelectField.tableAdapter(serviceProvider),
        switchboard: true,
        accordingTo: 'activity',
        url: serviceProvider.client.getAllPath,
        tempSchema: serviceProvider,
      },
      activity_status: {
        name: 'A', //Activity Status
        fieldRenderer: SelectFieldWithoutAutocomplete.tableAdapter(
          statuses.activityStatus
        ),
        switchboard: true,
        url: statuses.activityStatus.client.getAllPath,
        tempSchema: statuses.activityStatus,
      },
      bus_status: {
        name: 'B', //Bus Status
        fieldRenderer: SelectFieldWithoutAutocomplete.tableAdapter(
          statuses.bus
        ),
        switchboard: true,
        url: statuses.bus.client.getAllPath,
        tempSchema: statuses.bus,
      },
      stripper_status: {
        name: 'S', //Stripper Status
        fieldRenderer: SelectFieldWithoutAutocomplete.tableAdapter(
          statuses.stripper
        ),
        switchboard: true,
        url: statuses.stripper.client.getAllPath,
        tempSchema: statuses.stripper,
        //readOnly: true,
      },
      sales_partner: {
        name: 'Sales Partner',
        fieldRenderer: SelectField.tableAdapter(salesPartner),
        switchboard: true,
        url: salesPartner.client.getAllPath,
        tempSchema: salesPartner,
      },
      sales_guide: {
        name: 'Sales Guide',
        fieldRenderer: SelectField.tableAdapter(enumItems.guide),
        switchboard: true,
        url: enumItems.guide.client.getAllPath,
        tempSchema: enumItems.guide,
      },
      extra_sales_guide_commission: {
        name: 'Extra Sales Guide Commission',
        fieldRenderer: FloatField,
        fieldType: 'number',
      },
      service_provider_status: {
        name: 'Service Provider Status',
        fieldRenderer: SelectFieldWithoutAutocomplete.tableAdapter(
          statuses.serviceProvider
        ),
        switchboard: true,
        url: statuses.serviceProvider.client.getAllPath,
        tempSchema: statuses.serviceProvider,
      },
      bus_provider: {
        name: 'Bus Provider',
        fieldRenderer: SelectField.tableAdapter(enumItems.busProvider),
        switchboard: true,
        url: enumItems.busProvider.client.getAllPath,
        tempSchema: enumItems.busProvider,
      },
      group_size: {
        name: 'Size',
        fieldRenderer: NumberField,
        fieldType: 'number',
      },
      date: {
        name: 'Date',
        fieldRenderer: DateField,
        fieldType: 'date',
      },
      meeting_time: {
        name: 'Meeting',
        fieldRenderer: TimeField,
        fieldType: 'time',
      },
      pickup_time: {
        name: 'Pickup',
        fieldRenderer: TimeField,
        fieldType: 'time',
      },
      start_time: {
        name: 'Start',
        fieldRenderer: TimeField,
        fieldType: 'time',
      },
      address_from: {
        name: 'From',
        fieldRenderer: SelectField.tableAdapter(address),
        switchboard: true,
        tempSchema: address,
        url: address.client.getAllPath,
      },
      address_to: {
        name: 'To',
        fieldRenderer: SelectField.tableAdapter(address),
        switchboard: true,
        tempSchema: address,
        url: address.client.getAllPath,
      },
      address_back_to: {
        name: 'Back To',
        fieldRenderer: SelectField.tableAdapter(address),
        switchboard: true,
        tempSchema: address,
        url: address.client.getAllPath,
      },
      bus_finish: {
        name: 'Bus Finish',
        fieldRenderer: TimeField,
        fieldType: 'time',
      },
      driver: {
        name: 'Driver',
        fieldRenderer: SelectField.tableAdapter(enumItems.driver),
        switchboard: true,
        url: enumItems.driver.client.getAllPath,
        tempSchema: enumItems.driver,
      },
      vehicle: {
        name: 'Vehicle',
        fieldRenderer: SelectField.tableAdapter(enumItems.vehicle),
        switchboard: true,
        url: enumItems.vehicle.client.getAllPath,
        tempSchema: enumItems.vehicle,
      },

      guide_costs: {
        name: 'Guide Costs',
        fieldRenderer: SetField.tableAdapter(guideCost),
        multiSwitchboard: true,
        url: guideCost.client.getAllPath,
        tempSchema: guideCost,
        fieldType: 'simpleSelect',
      },
      selling_price: {
        name: 'Selling Price',
        fieldRenderer: NumberField,
        fieldType: 'number',
      },
      discount_percent: {
        name: 'Discount Percent',
        fieldRenderer: NumberField,
        fieldType: 'number',
      },
      discount_value: {
        name: 'Discount Value',
        fieldRenderer: NumberField,
        fieldType: 'number',
      },
      guide_salary: {
        name: 'Guide Salary',
        fieldRenderer: NumberField,
        fieldType: 'number',
      },
      guide_work_time: {
        name: 'Guide Work Time',
        fieldRenderer: TimeField,
        fieldType: 'time',
      },
      guide_commission: {
        name: 'Guide Commission',
        fieldRenderer: NumberField,
        fieldType: 'number',
      },
      date_added: {
        name: 'Date Added',
        fieldRenderer: DateField,
        fieldType: 'date',
      },
      guide_notes: {
        name: 'Guide Notes',
        fieldRenderer: TextField,
        fieldType: 'textarea',
      },
      booking_manager_info: {
        name: 'Booking Manager Info',
        fieldRenderer: TextField,
        fieldType: 'textarea',
      },
      activity_reservation_costs: {
        name: 'Reservation Costs',
        fieldRenderer: SetField.tableAdapter(activityReservationCost),
        multiSwitchboard: true,
        url: activityReservationCost.client.getAllPath,
        tempSchema: activityReservationCost,
        fieldType: 'simpleSelect',
      },
      notes: {
        name: 'Notes',
        fieldRenderer: TextField,
        fieldType: 'textarea',
      },
      notes_partner: {
        name: 'Notes Partner',
        fieldRenderer: TextField,
        fieldType: 'textarea',
      },
      partner_daytime: {
        name: 'Partner Daytime',
        fieldRenderer: SelectField.tableAdapter(enumItems.partnerDaytime),
        switchboard: true,
        url: enumItems.partnerDaytime.client.getAllPath,
        tempSchema: enumItems.partnerDaytime,
      },
      id: {
        name: 'ID',
        required: true,
        fieldRenderer: NumberField,
        fieldType: 'number',
        readOnly: true,
      },
    },
    client: new Client(
      '/stripper-reservations',
      forGroupId !== undefined
        ? `/groups/${forGroupId}/stripper-reservations`
        : undefined
    ),
  };
};
